/**
 * PRODUCT_DEV-679: 未保存の編集を失わせないためのガード（React 非依存の中核）。
 *
 * `useUnsavedChangesGuard` から使う純ロジックをここに置く。shared-ui の vitest は
 * node 環境（testing-library なし）のため、フック本体は単体テストできない。
 * テストできる形にするために「window を引数で受け取る」形に切り出してある。
 */

/** `beforeunload` を張るのに必要な最小限の window 形状。 */
export interface BeforeUnloadTarget {
  addEventListener(
    type: "beforeunload",
    listener: (event: BeforeUnloadEventLike) => void,
  ): void;
  removeEventListener(
    type: "beforeunload",
    listener: (event: BeforeUnloadEventLike) => void,
  ): void;
}

/**
 * `beforeunload` イベントの最小形状。
 * 現行ブラウザはカスタム文言を出さないが、`preventDefault()` と
 * `returnValue` の両方を設定しないと確認ダイアログ自体が出ない実装が残っている
 * （Chrome / Edge < 119）。
 */
export interface BeforeUnloadEventLike {
  preventDefault(): void;
  returnValue?: unknown;
}

/**
 * リロード / タブ閉じ / 他オリジンへの遷移に対して確認ダイアログを出す。
 *
 * `getIsDirty` は登録時ではなく **発火時** に評価する。dirty が変わるたびに
 * リスナを張り替えると、その間に発生した unload を取りこぼすため。
 *
 * @returns 解除関数
 */
export function attachBeforeUnloadGuard(
  target: BeforeUnloadTarget,
  getIsDirty: () => boolean,
): () => void {
  const listener = (event: BeforeUnloadEventLike): void => {
    if (!getIsDirty()) return;
    event.preventDefault();
    // 旧仕様のブラウザ（Chrome / Edge < 119）向けの後方互換。
    // MDN: `returnValue` の初期値は空文字で、**truthy を代入したときだけ**ダイアログが出る。
    // 空文字を入れると「確認しない」を明示するのと同じで意味がないため true を入れる。
    // https://developer.mozilla.org/en-US/docs/Web/API/BeforeUnloadEvent/returnValue
    event.returnValue = true;
  };
  target.addEventListener("beforeunload", listener);
  return () => target.removeEventListener("beforeunload", listener);
}

/** `shouldBlockNavigation` の判定材料（react-router の BlockerFunction 引数の部分形）。 */
export interface NavigationCandidate {
  currentLocation: { pathname: string; search?: string };
  nextLocation: { pathname: string; search?: string };
}

/**
 * アプリ内遷移をブロックすべきかを判定する。
 *
 * 同一パスへの遷移（クエリだけ変わる自己遷移。`navigate(pathname, { replace: true })`
 * で state を消すパターンが辞書詳細などに実在する）はブロックしない。
 * ここで弾かないと、画面が自分自身に遷移しただけで確認ダイアログが出る。
 */
export function shouldBlockNavigation(
  isDirty: boolean,
  { currentLocation, nextLocation }: NavigationCandidate,
): boolean {
  if (!isDirty) return false;
  return currentLocation.pathname !== nextLocation.pathname;
}
